import { fetchPayoutFee } from '@api';
import { verifyWalletAddress } from '@api/payout';
import { Button, Dropdown, FormInput } from '@basqet-dashboard/common/components';
import { useCurrencies } from '@basqet-dashboard/common/hooks';
import {
  formatErrorMessage,
  singleTransferSchema,
  useNavigateTo
} from '@basqet-dashboard/common/utils';
import { useFetchBeneficiaries, useFetchPayoutWallets } from '@hooks/queries';
import { BackButton } from '@pages/payout/back-button';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { SelectedWallet } from '../../selected-wallet';

const SelectAccount = ({ setCurrentStage, transactionInfo, setTransactionInfo }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formIsValidated, setFormIsValidated] = useState(false);
  const { navigateTo } = useNavigateTo();
  const { cryptoCurrencies } = useCurrencies();

  const { wallets } = useFetchPayoutWallets();

  const handleChange = (e) => {
    const { name, value } = e.target;

    singleTransferSchema
      .validate({ ...transactionInfo, [name]: value }, { abortEarly: false })
      .then(() => {
        setFormErrors({});
        setTransactionInfo(() => {
          return { ...transactionInfo, [name]: value };
        });
        setFormIsValidated(true);
      })
      .catch((error) => {
        setFormIsValidated(false);
        error.inner.forEach((e) => {
          const { message, path } = e;
          if (path === name) {
            setFormErrors({ ...formErrors, [path]: message });
            setTransactionInfo({ ...transactionInfo, [name]: '' });
          } else {
            if (formErrors[name]) {
              formErrors[name] = null;
            }
            setTransactionInfo(() => {
              return { ...transactionInfo, [name]: value };
            });
          }
        });
      });
  };

  const { currency_id: currencyId, address, amount, recipient } = transactionInfo;

  const { beneficiaries } = useFetchBeneficiaries({
    grouped: false,
    dropdown: true,
    singles_only: true
  });

  useEffect(() => {
    if (recipient === address) {
      setTransactionInfo({ ...transactionInfo, address: '' });
    }
  }, [recipient]);

  const beneficiariesOptions = beneficiaries
    .map((beneficiary) => ({
      ...beneficiary,
      label: `${beneficiary.alias} ${
        beneficiary.currency_slug ? `(${beneficiary.currency_slug})` : ''
      }`,
      value: beneficiary.address
    }))
    .filter(
      (beneficiary) =>
        parseInt(beneficiary.currency_id) === parseInt(currencyId) || !beneficiary.currency_id
    );

  /*
   * When form is submitted
   */
  const onSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    const { recipient } = transactionInfo;
    const transactionData = { ...transactionInfo };

    transactionData.address = recipient !== '0' ? recipient : address;

    try {
      const { data: walletAddressData } = await verifyWalletAddress({
        currency_slug: selectedWallet?.currency?.slug,
        wallet_address: transactionData?.address
      });

      if (walletAddressData) {
        const { data: payoutFeeData } = await fetchPayoutFee({
          currency_id: transactionData.currency_id,
          amount: transactionData.amount
        });
        const { amount, transactionFee, totalAmount } = payoutFeeData;
        setTransactionInfo(() => {
          return { ...transactionData, amount, fee: transactionFee, totalAmount };
        });
        setCurrentStage('CONFIRM_TRANSACTION');
      }
    } catch (error) {
      if (error.response.data.message.includes('address')) {
        setFormErrors({
          address: 'Invalid Wallet Address'
        });
        setFormIsValidated(false);
      } else {
        formatErrorMessage(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectedWallet = wallets.find(
    (wallet) => parseInt(wallet.currency_id) === parseInt(currencyId)
  );

  const { available_balance = 0 } = selectedWallet ?? {};
  const amountError = parseInt(amount) > parseInt(available_balance);

  const currencyIsSelected = Boolean(currencyId);

  return (
    <div className="card">
      <BackButton onClick={() => navigateTo('/payouts')} />

      <div className="card__header">
        <h3 className="card__title">
          Active <span className="text-cursive">Account</span>
        </h3>
      </div>
      <form onSubmit={onSubmit}>
        <SelectedWallet currencyId={currencyId} />
        <div className="select__account__form">
          <Dropdown
            formLabel="Payment Currency"
            placeholder={'Select Payment Currency'}
            name="currency_id"
            id="currency_id"
            options={cryptoCurrencies}
            defaultValue={parseInt(currencyId)}
            register={{ onChange: handleChange }}
            error={formErrors?.currency_id}
          />

          <Dropdown
            placeholder="Select Recipient"
            formLabel="Recipient"
            name="recipient"
            id="recipient"
            register={{ onChange: handleChange }}
            defaultValue={recipient}
            options={beneficiariesOptions}
            key={`recipient-${currencyId}`}
            readOnly={!currencyIsSelected}
          />

          {recipient === '0' && (
            <FormInput
              placeholder="Enter wallet address"
              name="address"
              id="address"
              label="Wallet Address"
              type="text"
              defaultValue={address}
              register={{ onChange: handleChange }}
              disabled={!currencyIsSelected}
              error={formErrors?.address}
            />
          )}

          <FormInput
            placeholder="Enter amount"
            name="amount"
            label="Amount"
            type="number"
            step="any"
            min="0"
            defaultValue={amount}
            register={{ onChange: handleChange }}
            error={amountError ? 'Amount cannot more than the wallet balance' : formErrors?.amount}
            disabled={!currencyIsSelected}
          />

          <Button
            isFull
            disabled={!formIsValidated || amountError || !amount}
            type="submit"
            isLoading={isLoading}>
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

SelectAccount.propTypes = {
  setCurrentStage: PropTypes.func,
  setTransactionInfo: PropTypes.func,
  transactionInfo: PropTypes.object
};

export { SelectAccount };
